import { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import { RTCCodecAdapter } from '../adapter/rtc-codec';
import { HLSAdapter } from '../adapter/hls';
import { startPlaybackCamera } from '../../../socket/playback';
import { STREAM_MODE } from '../utils';
import { beeXLogger } from '../../../helpers/logger';
import {
  useRetryManager,
  MAX_TRANSCODE_RETRIES,
  MAX_HLS_RETRIES,
  MAX_TRANSCODE_REFETCH,
} from './useRetryManager';

export const useStreamPlayer = ({
  serial,
  namespace,
  autoPlay,
  activeQueryTime,
  selectedDate,
  playerMode,
  isMountedRef,
  isSeekingRef,
  videoRef,
  canvasRef,
  playerRef,
  setVideo,
  video,
  videoDurationRef,
}) => {
  const [playbackStream, setPlaybackStream] = useState(null);
  const [output, setOutput] = useState(null);
  const [streamMode, setStreamMode] = useState(STREAM_MODE.NORMAL);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(null);
  const [videoError, setVideoError] = useState(null);

  const adapterRef = useRef(null);
  const lastStreamTokenRef = useRef(null);
  const fetchStreamRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastPlayedSecondsRef = useRef(0);
  const streamModeRef = useRef(STREAM_MODE.NORMAL);
  const lastPlayableModeRef = useRef(STREAM_MODE.NORMAL);

  const {
    retryRef,
    getErrorKey,
    isSingleRetryNormalError,
    resetNormalMode: resetNormalModeRetry,
    resetAll: resetAllRetry,
    resetTranscode: resetTranscodeState,
  } = useRetryManager();

  const handleProgress = useCallback(
    (playedSeconds) => {
      const dur = videoDurationRef.current;
      if (!dur) return;
      setIsLoading(false);
      lastPlayableModeRef.current = streamModeRef.current;
      retryRef.current = {
        ...retryRef.current,
        hlsCount: 0,
        refetchCount: 0,
      };

      const finalPlayedSeconds = lastPlayedSecondsRef.current + playedSeconds;

      if (finalPlayedSeconds >= dur) {
        setIsLoading(false);
        setLoadingStage(null);
        if (videoRef.current) {
          if (videoRef.current.currentTime !== undefined) {
            videoRef.current.currentTime = dur;
          }
          if (typeof videoRef.current.pause === 'function') {
            videoRef.current.pause();
          }
          videoRef.current.srcObject = null;
        }

        if (playerRef.current) {
          if (typeof playerRef.current.getInternalPlayer === 'function') {
            const internalPlayer = playerRef.current.getInternalPlayer();
            if (internalPlayer && typeof internalPlayer.pause === 'function') {
              internalPlayer.pause();
            }
          } else if (typeof playerRef.current.pause === 'function') {
            playerRef.current.pause();
          }
        }

        if (adapterRef.current) {
          try {
            adapterRef.current.destroy();
          } catch (error) {}
          adapterRef.current = null;
        }
        lastStreamTokenRef.current = null;

        setVideo((p) => ({ ...p, played: 1, ended: true, playing: false }));
        setOutput(null);
        return;
      }

      setVideo((p) => ({
        ...p,
        played: finalPlayedSeconds / dur,
        playing: true,
      }));
    },
    [videoDurationRef, setVideo, videoRef, playerRef, retryRef],
  );

  useEffect(() => {
    streamModeRef.current = streamMode;
  }, [streamMode]);

  const showFinalError = useCallback(() => {
    if (!isMountedRef.current) return;
    isSeekingRef.current = false;
    setIsLoading(false);
    setLoadingStage(null);
    setVideoError("Can't play this video");
    setVideo((p) => ({ ...p, playing: false }));
  }, [isMountedRef, isSeekingRef, setVideo]);

  const resetStreamState = useCallback(
    (caller) => {
      beeXLogger.debug(`Resetting stream state, called by ${caller}`);
      isFetchingRef.current = false;
      resetAllRetry();
      setVideoError(null);
      // setStreamMode(STREAM_MODE.NORMAL);
      setOutput(null);
      setPlaybackStream(null);
      setLoadingStage(null);
      if (adapterRef.current) {
        try {
          adapterRef.current.destroy();
        } catch (_) {}
        adapterRef.current = null;
      }
      lastStreamTokenRef.current = null;
    },
    [resetAllRetry],
  );

  const resetTranscodeRetry = useCallback(() => {
    resetTranscodeState();
    setVideoError(null);
  }, [resetTranscodeState]);

  const resetRetryStateAfterPlaybackStarts = useCallback(() => {
    retryRef.current = {
      ...retryRef.current,
      normalMode: { key: null, retried: false },
      hasTranscodeRetry: false,
      transcodeTimeout: false,
      retryTime: null,
      transcodeCount: 0,
      hlsCount: 0,
      refetchCount: 0,
    };
  }, [retryRef]);

  const markPlaybackStable = useCallback(() => {
    lastPlayableModeRef.current = streamModeRef.current;
    resetRetryStateAfterPlaybackStarts();
  }, [resetRetryStateAfterPlaybackStarts]);

  const queueTranscodeRetry = useCallback(
    (time) => {
      const r = retryRef.current;
      if (r.transcodeTimeout) {
        showFinalError();
        return;
      }
      if (r.hasTranscodeRetry) return;
      if (r.transcodeCount >= MAX_TRANSCODE_RETRIES) {
        showFinalError();
        setLoadingStage(null);
        setIsLoading(false);
        setVideo((p) => ({ ...p, playing: false }));
        return;
      }

      retryRef.current.transcodeCount += 1;
      retryRef.current.hasTranscodeRetry = true;
      retryRef.current.retryTime = time;
      isFetchingRef.current = false;

      setPlaybackStream(null);
      adapterRef.current = null;
      lastStreamTokenRef.current = null;
      setOutput(null);
      setIsLoading(true);
      streamModeRef.current = STREAM_MODE.TRANSCODE;
      setStreamMode(STREAM_MODE.TRANSCODE);
    },
    [retryRef, showFinalError],
  );

  const retryPlaybackByCurrentLink = useCallback(
    (currentOutput = null) => {
      if (!isMountedRef.current) return;
      setIsLoading(true);

      const r = retryRef.current;
      const useTranscode = streamModeRef.current === STREAM_MODE.TRANSCODE;
      const fallbackTime = r.retryTime ?? moment(activeQueryTime).unix();

      if (typeof currentOutput === 'string') {
        if (r.hlsCount >= MAX_HLS_RETRIES) {
          retryRef.current.hlsCount = 0;

          if (useTranscode) {
            if (r.refetchCount >= MAX_TRANSCODE_REFETCH) {
              showFinalError();
              return;
            }
            retryRef.current.refetchCount += 1;
            retryRef.current.transcodeCount = 0;
            retryRef.current.hasTranscodeRetry = true;
            isFetchingRef.current = false;
            setPlaybackStream(null);
            setOutput(null);
            adapterRef.current = null;
            lastStreamTokenRef.current = null;
            streamModeRef.current = STREAM_MODE.NORMAL;
            setStreamMode(STREAM_MODE.NORMAL);
            setTimeout(() => {
              if (isMountedRef.current) {
                streamModeRef.current = STREAM_MODE.TRANSCODE;
                setStreamMode(STREAM_MODE.TRANSCODE);
              }
            }, 0);
            return;
          }

          queueTranscodeRetry(fallbackTime);
          return;
        }

        retryRef.current.hlsCount += 1;
        setOutput(null);
        const timeoutId = setTimeout(() => {
          if (isMountedRef.current) setOutput(currentOutput);
        }, 2000);
        return () => clearTimeout(timeoutId);
      }

      retryRef.current.hlsCount = 0;

      if (playbackStream) {
        setOutput(null);
        adapterRef.current = null;
        lastStreamTokenRef.current = null;
        setPlaybackStream((prev) => (prev ? { ...prev } : prev));
        return;
      }

      if (useTranscode) {
        retryRef.current.transcodeCount = 0;
        retryRef.current.hasTranscodeRetry = true;
        isFetchingRef.current = false;
        streamModeRef.current = STREAM_MODE.NORMAL;
        setStreamMode(STREAM_MODE.NORMAL);
        setTimeout(() => {
          if (isMountedRef.current) {
            streamModeRef.current = STREAM_MODE.TRANSCODE;
            setStreamMode(STREAM_MODE.TRANSCODE);
          }
        }, 0);
        return;
      }

      queueTranscodeRetry(fallbackTime);
    },
    [
      isMountedRef,
      retryRef,
      streamModeRef,
      playbackStream,
      activeQueryTime,
      queueTranscodeRetry,
      showFinalError,
    ],
  );

  const restartPlayback = useCallback(
    (preferredMode = null) => {
      if (!isMountedRef.current) return;

      const nextMode =
        preferredMode ||
        lastPlayableModeRef.current ||
        streamModeRef.current ||
        STREAM_MODE.NORMAL;

      resetStreamState('restartPlayback');
      setVideoError(null);
      setIsLoading(true);
      streamModeRef.current = nextMode;
      setStreamMode(nextMode);
      resetRetryStateAfterPlaybackStarts();

      setTimeout(() => {
        if (isMountedRef.current) {
          fetchStreamRef.current(false, nextMode);
        }
      }, 0);
    },
    [
      isMountedRef,
      resetStreamState,
      setVideoError,
      setIsLoading,
      resetRetryStateAfterPlaybackStarts,
    ],
  );

  const retryNormalOnceOrFallbackTranscode = useCallback(
    (err, time, fallback, retryFn) => {
      const r = retryRef.current;
      if (r.hasTranscodeRetry || r.transcodeTimeout) return false;

      const errorKey = getErrorKey(err, fallback);
      const prev = r.normalMode;
      const shouldUseSingleRetry =
        prev.key === errorKey ||
        isSingleRetryNormalError(errorKey) ||
        !prev.retried;

      if (shouldUseSingleRetry && !prev.retried) {
        retryRef.current.normalMode = { key: errorKey, retried: true };
        if (typeof retryFn === 'function') {
          retryFn();
        } else {
          setTimeout(() => {
            fetchStreamRef.current(true);
          }, 0);
        }
        return true;
      }

      queueTranscodeRetry(time ?? r.retryTime);
      return true;
    },
    [retryRef, getErrorKey, isSingleRetryNormalError, queueTranscodeRetry],
  );

  const requestStreamRefetch = useCallback((reason = '') => {
    if (!fetchStreamRef.current) return;
    beeXLogger.debug('Refetching playback stream:', reason);
    isFetchingRef.current = false;
    fetchStreamRef.current(true);
  }, []);

  const fetchStream = useCallback(
    (isErrorRetry = false, preferredMode = null) => {
      if (!serial || !namespace || !autoPlay) return;
      if (isFetchingRef.current) return;

      const r = retryRef.current;
      const useTranscode =
        (preferredMode || streamModeRef.current) === STREAM_MODE.TRANSCODE;

      if (!isErrorRetry) resetNormalModeRetry();

      if (useTranscode && r.transcodeTimeout) {
        showFinalError();
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      setLoadingStage(1);
      setOutput(null);
      setPlaybackStream(null);
      adapterRef.current = null;
      lastStreamTokenRef.current = null;
      lastPlayedSecondsRef.current = videoDurationRef.current
        ? videoDurationRef.current * (video.played || 0)
        : 0;
      const datePart = moment(selectedDate);
      const timePart = moment(activeQueryTime);
      const queryTime = datePart
        .clone()
        .set({
          hour: timePart.hour(),
          minute: timePart.minute(),
          second: timePart.second(),
        })
        .format('YYYY-MM-DD HH:mm:ss');

      const params = {
        serial,
        namespace,
        queryTime,
        serviceId: 4,
        type: 'external',
      };
      if (useTranscode) params.functions = 'transcode_h264';

      if (!isSeekingRef.current) {
        setVideo((p) => ({ ...p, played: 0, playing: true, ended: false }));
      } else {
        setVideo((p) => ({ ...p, playing: true }));
      }

      beeXLogger.debug('🚀 ~ fetchStream ~ params:', params);
      startPlaybackCamera(params)
        .then((res) => {
          beeXLogger.debug('🚀 ~ fetchStream ~ res:', res);
          isFetchingRef.current = false;
          isSeekingRef.current = false;
          if (!isMountedRef.current) return;
          retryRef.current.retryTime = moment(activeQueryTime).unix();
          setLoadingStage(2);
          setPlaybackStream(res);
        })
        .catch((err) => {
          isFetchingRef.current = false;
          if (!isMountedRef.current) return;
          beeXLogger.error('startPlaybackCamera err:', params.functions, err);
          setLoadingStage(null);

          if (useTranscode) {
            const isTimeout =
              err?.code === 'TIMEOUT' || err?.message === 'Request timed out';
            if (isTimeout) {
              retryRef.current.transcodeTimeout = true;
              retryRef.current.hasTranscodeRetry = true;
            }
            showFinalError();
            return;
          }

          const handled = retryNormalOnceOrFallbackTranscode(
            err,
            moment(activeQueryTime).unix(),
            'Failed to request playback stream',
          );
          if (!handled && isMountedRef.current) setIsLoading(false);
        });
    },
    [
      serial,
      namespace,
      autoPlay,
      retryRef,
      streamModeRef,
      resetNormalModeRetry,
      videoDurationRef,
      video.played,
      selectedDate,
      activeQueryTime,
      isSeekingRef,
      showFinalError,
      setVideo,
      isMountedRef,
      retryNormalOnceOrFallbackTranscode,
    ],
  );

  useEffect(() => {
    fetchStreamRef.current = fetchStream;
  }, [fetchStream]);

  useEffect(() => {
    isFetchingRef.current = false;
  }, [serial, activeQueryTime]);

  useEffect(() => {
    if (playerMode === 'file') return;
    if (!serial || !namespace || !autoPlay) return;
    beeXLogger.debug('Fetching stream:', {
      serial,
      namespace,
      queryTime: activeQueryTime,
    });
    resetTranscodeRetry();
    fetchStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, serial, activeQueryTime, autoPlay, playerMode]);

  useEffect(() => {
    if (playerMode === 'file') return;
    if (streamMode !== STREAM_MODE.TRANSCODE) return;
    if (!retryRef.current.hasTranscodeRetry) return;
    if (retryRef.current.transcodeTimeout) {
      showFinalError();
      return;
    }
    fetchStreamRef.current(false, STREAM_MODE.TRANSCODE);
  }, [streamMode, showFinalError, playerMode, retryRef]);

  useEffect(() => {
    if (!playbackStream) return;
    adapterRef.current =
      playbackStream.protocol === 'webrtc'
        ? new RTCCodecAdapter()
        : new HLSAdapter();
    return () => {
      if (adapterRef.current) {
        try {
          adapterRef.current.destroy();
        } catch (e) {
          beeXLogger.error('Error destroying adapter:', e);
        }
      }
      adapterRef.current = null;
      lastStreamTokenRef.current = null;
    };
  }, [playbackStream]);

  useEffect(() => {
    const adapter = adapterRef.current;
    if (!adapter || !playbackStream) return;
    if (lastStreamTokenRef.current === playbackStream.streamToken) return;

    lastStreamTokenRef.current = playbackStream.streamToken;

    adapter.connect(
      { ...playbackStream, streamMode: streamModeRef.current },
      { streamToken: playbackStream.streamToken },
      (out) => {
        if (!isMountedRef.current) return;
        if (!out) {
          retryNormalOnceOrFallbackTranscode(
            new Error('No stream output'),
            null,
            'No stream output',
            () => retryPlaybackByCurrentLink(null),
          );
          return;
        }
        setLoadingStage(3);
        setIsLoading(true);
        setOutput(out);
      },
      (err) => {
        if (!isMountedRef.current) return;
        beeXLogger.error('Adapter connect error:', err);
        setLoadingStage(null);
        if (streamModeRef.current === STREAM_MODE.TRANSCODE) {
          retryPlaybackByCurrentLink(null);
        } else {
          const handled = retryNormalOnceOrFallbackTranscode(
            err,
            null,
            'Client failed to connect stream',
            () => retryPlaybackByCurrentLink(null),
          );
          if (!handled && isMountedRef.current) setIsLoading(false);
        }
      },
    );

    return () => {
      try {
        if (adapter) adapter.destroy();
      } catch (e) {
        beeXLogger.error('Error destroying adapter in connect:', e);
      }
    };
  }, [
    playbackStream,
    streamMode,
    isMountedRef,
    retryNormalOnceOrFallbackTranscode,
    retryPlaybackByCurrentLink,
  ]);

  useEffect(() => {
    if (!(output instanceof MediaStream) || !videoRef.current) return;
    const el = videoRef.current;
    el.srcObject = output;

    const offLoading = () => {
      if (!isMountedRef.current) return;
      setLoadingStage(4);
      setTimeout(() => {
        if (isMountedRef.current) setLoadingStage(null);
      }, 1500);
      setIsLoading(false);
    };
    const onMeta = () => {
      if (!isMountedRef.current) return;
      const w = el.videoWidth;
      if (canvasRef.current) {
        canvasRef.current.width = w;
        canvasRef.current.height = parseInt(w / (w / el.videoHeight), 10);
      }
    };
    const onTimeUpdate = () => {
      if (!isMountedRef.current) return;
      if (el.currentTime > 0) {
        markPlaybackStable();
      }
      handleProgress(el.currentTime);
    };

    el.addEventListener('canplay', offLoading);
    el.addEventListener('playing', offLoading);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('timeupdate', onTimeUpdate);
    return () => {
      el.removeEventListener('canplay', offLoading);
      el.removeEventListener('playing', offLoading);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [output, isMountedRef, videoRef, canvasRef, handleProgress]);

  useEffect(() => {
    if (typeof output !== 'string' || !playerRef.current) return;
    const root = playerRef.current;
    const v =
      typeof root.getInternalPlayer === 'function'
        ? root.getInternalPlayer()
        : root;
    if (!v || typeof v.addEventListener !== 'function') return;
    const onMeta = () => {
      if (!isMountedRef.current) return;
      const w = v.videoWidth;
      if (canvasRef.current) {
        canvasRef.current.width = w;
        canvasRef.current.height = parseInt(w / (w / v.videoHeight), 10);
      }
    };
    v.addEventListener('loadedmetadata', onMeta);
    return () => v.removeEventListener('loadedmetadata', onMeta);
  }, [output, isMountedRef, playerRef, canvasRef]);

  const handleReady = useCallback(() => {
    if (!isMountedRef.current) return;
    setLoadingStage(4);
    setTimeout(() => {
      if (isMountedRef.current) setLoadingStage(null);
    }, 1500);
    setIsLoading(false);
    markPlaybackStable();
  }, [isMountedRef, markPlaybackStable]);

  const handleStart = useCallback(() => {
    if (isMountedRef.current) setIsLoading(false);
  }, [isMountedRef]);

  return {
    playbackStream,
    output,
    streamMode,
    isLoading,
    setIsLoading,
    loadingStage,
    videoError,
    setVideoError,
    resetStreamState,
    resetTranscodeRetry,
    showFinalError,
    retryPlaybackByCurrentLink,
    retryNormalOnceOrFallbackTranscode,
    requestStreamRefetch,
    restartPlayback,
    handleProgress,
    handleReady,
    handleStart,
  };
};
